import {
  BIG_JOKER, RANK_2, RANK_A,
  buildDeck, isWildcard, rankOf, singleOrder, sortHand, suitOf,
  type Card, type Rank,
} from "./cards.js";
import {
  ComboType, beats, canBeat, classifyAll, comboTypeLabel, type Combo,
} from "./combos.js";
import { legalPlays } from "./enumerate.js";
import { makeRng } from "./rng.js";
import type { Ruleset } from "./ruleset.js";

export type Seat = 0 | 1 | 2 | 3;
export type Team = 0 | 1;

export const SEATS: readonly Seat[] = [0, 1, 2, 3];
export const HAND_SIZE = 27;

export const teamOf = (seat: Seat): Team => (seat % 2) as Team;
export const partnerOf = (seat: Seat): Seat => ((seat + 2) % 4) as Seat;
export const seatsOfTeam = (team: Team): [Seat, Seat] => [team as Seat, (team + 2) as Seat];

export type Phase = "returnTribute" | "playing" | "handOver" | "matchOver";

export interface TributeState {
  readonly resisted: boolean;
  /** Seats that owed tribute before a possible resistance. */
  readonly payers: Seat[];
  /** Cards already handed over, in the order they were paid. */
  readonly paid: { from: Seat; to: Seat; card: Card }[];
  /** Return payments still owed, oldest first. */
  readonly owed: { from: Seat; to: Seat }[];
  /** Who leads once the exchange finishes. */
  readonly leader: Seat;
}

export interface PublicAction {
  readonly seat: Seat;
  readonly kind: "play" | "pass";
  /** The play made, or the live play that this seat declined. */
  readonly combo: Combo | null;
  readonly trick: number;
}

export interface PublicTributeAction {
  readonly kind: "paid" | "returned";
  readonly from: Seat;
  readonly to: Seat;
  /** Null exists only in a redacted hypothetical state, never canonical play. */
  readonly card: Card | null;
}

export interface HandResult {
  readonly finished: Seat[];
  readonly unfinished: Seat[];
  readonly winningTeam: Team;
  readonly losingTeam: Team;
  readonly upgrade: number;
  readonly levelBefore: Rank;
  readonly levelAfter: Rank;
  readonly matchWon: boolean;
  /** Won the hand at level A but missed the win requirement, so stayed at A. */
  readonly heldAtA: boolean;
  readonly aAttemptFailed: boolean;
  readonly droppedToLevel: Rank | null;
}

export interface MatchState {
  readonly seed: string;
  readonly rules: Ruleset;
  phase: Phase;
  handNumber: number;
  levels: [Rank, Rank];
  aAttempts: [number, number];
  /** The team whose level card is in play this hand. */
  playingTeam: Team;
  level: Rank;
  hands: Card[][];
  /** Every card played this hand, in order — the public record. */
  discard: Card[];
  /** Attributed play/pass history for opponent modelling. */
  log: PublicAction[];
  /** One-based number of the live trick. */
  trickNumber: number;
  /** Public tribute history. Return cards are redacted in seat views. */
  tributeLog: PublicTributeAction[];
  turn: Seat;
  current: { seat: Seat; combo: Combo } | null;
  /** Passes since the last play. */
  passes: number;
  finished: Seat[];
  out: boolean[];
  tribute: TributeState | null;
  lastResult: HandResult | null;
  winner: Team | null;
}

/**
 * What the engine reports. The Game Room wraps these with sequence numbers,
 * render timing and per-seat redaction before they reach a client.
 *
 * Contract worth knowing: a hand that ends mid-trick — which is most of them,
 * since the hand stops the moment both members of a team are out — emits
 * `handEnded` with no preceding `trickEnded`. Anything tracking trick ownership
 * must therefore reset on `handStarted`, not only on `trickEnded`.
 */
export type GameEvent =
  | { type: "handStarted"; handNumber: number; level: Rank; playingTeam: Team }
  | { type: "dealt"; hands: Card[][] }
  | { type: "tributeResisted"; seats: Seat[] }
  | { type: "tributePaid"; from: Seat; to: Seat; card: Card }
  | { type: "tributeReturned"; from: Seat; to: Seat; card: Card }
  | { type: "turnChanged"; seat: Seat; open: boolean; phase: Phase }
  | { type: "played"; seat: Seat; cards: Card[]; combo: Combo; remaining: number }
  | { type: "passed"; seat: Seat }
  | { type: "trickEnded"; winner: Seat; leader: Seat; jiefeng: boolean }
  | { type: "playerOut"; seat: Seat; place: number }
  | { type: "handEnded"; result: HandResult }
  | { type: "matchEnded"; winner: Team };

export type Action =
  | { type: "play"; seat: Seat; cards: Card[] }
  | { type: "pass"; seat: Seat }
  | { type: "returnTribute"; seat: Seat; card: Card }
  | { type: "nextHand" };

export interface RuleViolation {
  readonly code: string;
  /** Plain-English explanation, shown directly by the client's rules coach. */
  readonly message: string;
}

export type ApplyResult =
  | { ok: true; events: GameEvent[] }
  | { ok: false; error: RuleViolation };

const fail = (code: string, message: string): ApplyResult => ({ ok: false, error: { code, message } });

/**
 * Announce whose turn it is.
 *
 * The phase matters as much as the seat: during `returnTribute` the player on
 * turn owes a card back rather than a play, and a client with only the seat has
 * no way to tell which action is being asked for.
 */
function turnEvent(state: MatchState): GameEvent {
  return {
    type: "turnChanged",
    seat: state.turn,
    open: state.current === null,
    phase: state.phase,
  };
}

// --- construction ------------------------------------------------------------

export interface MatchOptions {
  /**
   * What level each team starts on. Defaults to both teams at 2, a whole match.
   *
   * Starting both teams at A turns the match into the single hand that decides
   * it, which is how a fixed daily deal is expressed without inventing a second
   * way for a match to end: the engine still finishes it through `matchOver`,
   * so nothing downstream has to learn a new shape.
   */
  readonly levels?: readonly [Rank, Rank];
}

export function createMatch(
  seed: string, rules: Ruleset, options: MatchOptions = {},
): { state: MatchState; events: GameEvent[] } {
  const levels: [Rank, Rank] = [options.levels?.[0] ?? RANK_2, options.levels?.[1] ?? RANK_2];
  const state: MatchState = {
    seed,
    rules,
    phase: "playing",
    handNumber: 0,
    levels,
    aAttempts: [0, 0],
    playingTeam: 0,
    level: levels[0],
    hands: [[], [], [], []],
    discard: [],
    log: [],
    trickNumber: 1,
    tributeLog: [],
    turn: 0,
    current: null,
    passes: 0,
    finished: [],
    out: [false, false, false, false],
    tribute: null,
    lastResult: null,
    winner: null,
  };
  const events = startHand(state);
  return { state, events };
}

export function cloneState(state: MatchState): MatchState {
  return {
    ...state,
    levels: [...state.levels],
    aAttempts: [...state.aAttempts],
    hands: state.hands.map((h) => [...h]),
    discard: [...state.discard],
    log: state.log.map((entry) => ({
      ...entry,
      combo: entry.combo === null ? null : { ...entry.combo, cards: [...entry.combo.cards] },
    })),
    tributeLog: state.tributeLog.map((entry) => ({ ...entry })),
    finished: [...state.finished],
    out: [...state.out],
    current: state.current === null
      ? null
      : { seat: state.current.seat, combo: { ...state.current.combo, cards: [...state.current.combo.cards] } },
    tribute: state.tribute === null
      ? null
      : {
          ...state.tribute,
          payers: [...state.tribute.payers],
          paid: state.tribute.paid.map((entry) => ({ ...entry })),
          owed: state.tribute.owed.map((entry) => ({ ...entry })),
        },
  };
}

// --- hand setup --------------------------------------------------------------

function deal(state: MatchState): void {
  const rng = makeRng(`${state.seed}:hand:${state.handNumber}`);
  const deck = rng.shuffle(buildDeck());
  for (const seat of SEATS) {
    state.hands[seat] = sortHand(deck.slice(seat * HAND_SIZE, (seat + 1) * HAND_SIZE), state.level);
  }
}

/** Highest card in hand, never the wildcard — the forced tribute card. */
function tributeCardOf(hand: readonly Card[], level: Rank): Card {
  let best: Card | null = null;
  for (const card of hand) {
    if (isWildcard(card, level)) continue;
    if (best === null) { best = card; continue; }
    const d = singleOrder(rankOf(card), level) - singleOrder(rankOf(best), level);
    if (d > 0 || (d === 0 && suitOf(card) > suitOf(best))) best = card;
  }
  return best ?? (hand[0] as Card);
}

function moveCard(state: MatchState, from: Seat, to: Seat, card: Card): void {
  const source = state.hands[from] as Card[];
  const at = source.indexOf(card);
  if (at >= 0) source.splice(at, 1);
  (state.hands[to] as Card[]).push(card);
  state.hands[to] = sortHand(state.hands[to] as Card[], state.level);
}

function startHand(state: MatchState): GameEvent[] {
  state.handNumber += 1;
  state.level = state.levels[state.playingTeam];
  state.discard = [];
  state.log = [];
  state.trickNumber = 1;
  state.tributeLog = [];
  state.current = null;
  state.passes = 0;
  state.finished = [];
  state.out = [false, false, false, false];
  state.tribute = null;
  state.phase = "playing";

  deal(state);

  const events: GameEvent[] = [
    { type: "handStarted", handNumber: state.handNumber, level: state.level, playingTeam: state.playingTeam },
    { type: "dealt", hands: state.hands.map((h) => [...h]) },
  ];

  const previous = state.lastResult;
  if (previous === null) {
    // First hand of the match: the seed picks who leads.
    state.turn = makeRng(`${state.seed}:lead`).nextInt(4) as Seat;
    events.push(turnEvent(state));
    return events;
  }

  const winner = previous.finished[0] as Seat;
  if (!state.rules.tribute) {
    state.turn = winner;
    events.push(turnEvent(state));
    return events;
  }

  events.push(...openTribute(state, previous, winner));
  return events;
}

/**
 * Work out who owes tribute and either run the exchange or record a refusal.
 *
 * The losing team is the one without the hand winner. When both its players were
 * still holding cards at the end (双下) both pay, and the higher of the two cards
 * goes to the hand winner. Otherwise only the lower-placed loser pays.
 */
function openTribute(state: MatchState, previous: HandResult, winner: Seat): GameEvent[] {
  const events: GameEvent[] = [];
  const { losingTeam, finished, unfinished } = previous;
  const stillIn = unfinished.filter((s) => teamOf(s) === losingTeam);

  let payers: Seat[];
  if (stillIn.length === 2) payers = stillIn;
  else if (stillIn.length === 1) payers = [stillIn[0] as Seat];
  else {
    const losersByPlace = finished.filter((s) => teamOf(s) === losingTeam);
    const last = losersByPlace[losersByPlace.length - 1];
    payers = last === undefined ? [] : [last];
  }

  if (payers.length === 0) {
    state.turn = winner;
    events.push(turnEvent(state));
    return events;
  }

  // 抗贡 — the paying side refuses if it holds both big jokers between them.
  if (state.rules.resistTribute) {
    let bigJokers = 0;
    for (const payer of payers) {
      for (const card of state.hands[payer] as Card[]) {
        if (rankOf(card) === BIG_JOKER) bigJokers += 1;
      }
    }
    if (bigJokers >= 2) {
      state.tribute = { resisted: true, payers: [...payers], paid: [], owed: [], leader: winner };
      state.turn = winner;
      events.push({ type: "tributeResisted", seats: payers });
      events.push(turnEvent(state));
      return events;
    }
  }

  const offers = payers
    .map((from) => ({ from, card: tributeCardOf(state.hands[from] as Card[], state.level) }))
    .sort((a, b) => {
      const d = singleOrder(rankOf(b.card), state.level) - singleOrder(rankOf(a.card), state.level);
      return d !== 0 ? d : b.from - a.from;
    });

  const runnerUp = previous.finished[1];
  const recipients: Seat[] = offers.length === 2 && runnerUp !== undefined
    ? [winner, runnerUp]
    : [winner];

  const paid: { from: Seat; to: Seat; card: Card }[] = [];
  offers.forEach((offer, i) => {
    const to = recipients[i] ?? winner;
    moveCard(state, offer.from, to, offer.card);
    paid.push({ from: offer.from, to, card: offer.card });
    state.tributeLog.push({ kind: "paid", from: offer.from, to, card: offer.card });
    events.push({ type: "tributePaid", from: offer.from, to, card: offer.card });
  });

  // Whoever paid the hand winner leads once the returns are done.
  const leadPayer = paid.find((p) => p.to === winner)?.from ?? winner;
  const leader = state.rules.tributeLead === "tributePayer" ? leadPayer : winner;

  state.tribute = {
    resisted: false,
    payers: [...payers],
    paid,
    owed: paid.map((p) => ({ from: p.to, to: p.from })),
    leader,
  };
  state.phase = "returnTribute";
  const next = state.tribute.owed[0];
  state.turn = next === undefined ? leader : next.from;
  events.push(turnEvent(state));
  return events;
}

// --- play helpers ------------------------------------------------------------

function activeCount(state: MatchState): number {
  let n = 0;
  for (const seat of SEATS) if (!state.out[seat]) n += 1;
  return n;
}

function nextActive(state: MatchState, from: Seat): Seat {
  for (let i = 1; i <= 4; i++) {
    const seat = ((from + i) % 4) as Seat;
    if (!state.out[seat]) return seat;
  }
  return from;
}

/** A hand is over the moment both members of either team have gone out. */
function handIsOver(state: MatchState): boolean {
  for (const team of [0, 1] as Team[]) {
    const [a, b] = seatsOfTeam(team);
    if (state.out[a] && state.out[b]) return true;
  }
  return false;
}

function removeCards(hand: Card[], cards: readonly Card[]): boolean {
  const copy = [...hand];
  for (const card of cards) {
    const at = copy.indexOf(card);
    if (at < 0) return false;
    copy.splice(at, 1);
  }
  hand.length = 0;
  hand.push(...copy);
  return true;
}

/** Why the attempted cards cannot take the trick — used verbatim by the coach. */
function explainCannotBeat(current: Combo, attempted: readonly Combo[]): string {
  const shape = comboTypeLabel(current.type);
  if (current.power > 0) {
    return `That trick is held by a ${shape}. Only a stronger bomb can take it.`;
  }
  const sameShape = attempted.some((c) => c.type === current.type && c.length === current.length);
  if (sameShape) return `Your ${shape} is too low — it has to be higher than the one on the table.`;
  return `A ${shape} can only be beaten by a higher ${shape}, or by a bomb.`;
}

// --- the reducer -------------------------------------------------------------

/**
 * Apply an action, mutating `state` in place and returning what happened.
 *
 * Mutation is deliberate: the authoritative server keeps exactly one state per
 * room and replays actions into it, and the AI arena runs millions of these.
 * Callers that need a before-image should use `cloneState` first.
 */
export function apply(state: MatchState, action: Action): ApplyResult {
  switch (action.type) {
    case "play":
    case "pass":
      return applyPlayOrPass(state, action);
    case "returnTribute":
      return applyReturnTribute(state, action);
    case "nextHand":
      if (state.phase !== "handOver") return fail("wrong-phase", "The hand is still in progress.");
      return { ok: true, events: startHand(state) };
  }
}

function applyPlayOrPass(
  state: MatchState,
  action: { type: "play"; seat: Seat; cards: Card[] } | { type: "pass"; seat: Seat },
): ApplyResult {
  if (state.phase !== "playing") return fail("wrong-phase", "It is not time to play cards yet.");
  if (action.seat !== state.turn) return fail("not-your-turn", "It is not your turn.");

  const events: GameEvent[] = [];
  const hand = state.hands[action.seat] as Card[];

  if (action.type === "pass") {
    if (state.current === null) {
      return fail("cannot-pass-open-trick", "You are leading this trick, so you have to play something.");
    }
    state.passes += 1;
    state.log.push({
      seat: action.seat,
      kind: "pass",
      combo: state.current === null ? null : { ...state.current.combo, cards: [...state.current.combo.cards] },
      trick: state.trickNumber,
    });
    events.push({ type: "passed", seat: action.seat });
  } else {
    if (action.cards.length === 0) return fail("no-cards", "Choose at least one card.");
    const held = new Set(hand);
    const unique = new Set(action.cards);
    if (unique.size !== action.cards.length || [...unique].some((c) => !held.has(c))) {
      return fail("cards-not-held", "You do not hold those cards.");
    }

    const readings = classifyAll(action.cards, state.level, state.rules);
    if (readings.length === 0) {
      return fail("not-a-combo", "Those cards do not make a play. Try a single, a pair, a run, or a bomb.");
    }
    const combo = canBeat(action.cards, state.current?.combo ?? null, state.level, state.rules);
    if (combo === null) {
      const current = state.current;
      return fail(
        "cannot-beat",
        current === null ? "Those cards do not make a play." : explainCannotBeat(current.combo, readings),
      );
    }

    removeCards(hand, action.cards);
    state.discard.push(...action.cards);
    state.current = { seat: action.seat, combo };
    state.log.push({
      seat: action.seat,
      kind: "play",
      combo: { ...combo, cards: [...combo.cards] },
      trick: state.trickNumber,
    });
    state.passes = 0;
    events.push({
      type: "played",
      seat: action.seat,
      cards: [...action.cards],
      combo,
      remaining: hand.length,
    });

    if (hand.length === 0) {
      state.out[action.seat] = true;
      state.finished.push(action.seat);
      events.push({ type: "playerOut", seat: action.seat, place: state.finished.length });
    }
  }

  if (handIsOver(state)) {
    events.push(...finishHand(state));
    return { ok: true, events };
  }

  const current = state.current;
  if (current !== null) {
    const ownerActive = !state.out[current.seat];
    const responders = activeCount(state) - (ownerActive ? 1 : 0);
    if (state.passes >= responders) {
      // Everyone else passed. 接风: if the winner has gone out, their partner
      // picks up the lead and may open with anything.
      const winner = current.seat;
      let leader: Seat;
      let jiefeng = false;
      if (!state.out[winner]) leader = winner;
      else {
        const mate = partnerOf(winner);
        if (!state.out[mate]) { leader = mate; jiefeng = true; }
        else leader = nextActive(state, winner);
      }
      state.current = null;
      state.passes = 0;
      state.trickNumber += 1;
      state.turn = leader;
      events.push({ type: "trickEnded", winner, leader, jiefeng });
      events.push(turnEvent(state));
      return { ok: true, events };
    }
  }

  state.turn = nextActive(state, action.seat);
  events.push(turnEvent(state));
  return { ok: true, events };
}

function applyReturnTribute(
  state: MatchState,
  action: { type: "returnTribute"; seat: Seat; card: Card },
): ApplyResult {
  if (state.phase !== "returnTribute") return fail("wrong-phase", "No tribute is owed right now.");
  const tribute = state.tribute;
  const owed = tribute?.owed[0];
  if (tribute === null || tribute === undefined || owed === undefined) {
    return fail("wrong-phase", "No tribute is owed right now.");
  }
  if (action.seat !== owed.from) return fail("not-your-turn", "It is not your tribute to return.");
  const hand = state.hands[action.seat] as Card[];
  if (!hand.includes(action.card)) return fail("cards-not-held", "You do not hold that card.");
  if (!isLegalReturnTributeCard(hand, action.card, state.rules.maxReturnTributeRank)) {
    return fail(
      "return-tribute-too-high",
      `A returned card has to be a ${state.rules.maxReturnTributeRank} or lower.`,
    );
  }

  const events: GameEvent[] = [];
  moveCard(state, owed.from, owed.to, action.card);
  state.tributeLog.push({ kind: "returned", from: owed.from, to: owed.to, card: action.card });
  events.push({ type: "tributeReturned", from: owed.from, to: owed.to, card: action.card });
  tribute.owed.shift();

  const next = tribute.owed[0];
  if (next === undefined) {
    state.phase = "playing";
    state.turn = tribute.leader;
  } else {
    state.turn = next.from;
  }
  events.push(turnEvent(state));
  return { ok: true, events };
}

/**
 * A return must stay under the configured ceiling when the hand contains such
 * a card. If it does not, any held card is legal; otherwise a high-only hand
 * has no possible action and the tribute phase can never finish.
 */
export function isLegalReturnTributeCard(
  hand: readonly Card[], card: Card, maxRank: Rank,
): boolean {
  if (!hand.includes(card)) return false;
  return rankOf(card) <= maxRank || !hand.some((held) => rankOf(held) <= maxRank);
}

// --- scoring -----------------------------------------------------------------

/**
 * Does a hand won at level A actually take the match?
 *
 * `upgrade` encodes where the winner's partner finished: 3 = second,
 * 2 = third, 1 = fourth. The 2025 competition requirement is first place with
 * the partner in the top three, which is `upgrade >= 2`.
 */
export function meetsAWinRequirement(
  requirement: Ruleset["aWinRequirement"],
  upgrade: number,
): boolean {
  switch (requirement) {
    case "anyWin": return true;
    case "partnerTopThree": return upgrade >= 2;
    case "doubleDown": return upgrade === 3;
  }
}

function finishHand(state: MatchState): GameEvent[] {
  const finished = [...state.finished];
  const first = finished[0] as Seat;
  const winningTeam = teamOf(first);
  const losingTeam = (1 - winningTeam) as Team;
  const unfinished = SEATS.filter((s) => !state.out[s]);

  const partnerPlace = finished.indexOf(partnerOf(first));
  const upgrade = partnerPlace === 1 ? 3 : partnerPlace === 2 ? 2 : 1;

  const rules = state.rules;
  const levelBefore = state.levels[winningTeam];
  const atA = levelBefore === RANK_A;

  const matchWon = atA && meetsAWinRequirement(rules.aWinRequirement, upgrade);
  const heldAtA = atA && !matchWon;
  const levelAfter = atA ? RANK_A : Math.min(levelBefore + upgrade, RANK_A);
  state.levels[winningTeam] = levelAfter;
  if (levelAfter !== levelBefore) state.aAttempts[winningTeam] = 0;

  // House rule. The team whose level was in play is the one attempting A, and
  // any hand at A that does not convert into a match win burns an attempt —
  // including one they won but could not close out.
  const attempting = state.playingTeam;
  let aAttemptFailed = false;
  let droppedToLevel: Rank | null = null;
  if (!matchWon && rules.aAttempts > 0 && state.level === RANK_A) {
    aAttemptFailed = true;
    state.aAttempts[attempting] += 1;
    if (state.aAttempts[attempting] >= rules.aAttempts) {
      droppedToLevel = rules.aFailFallbackLevel;
      state.levels[attempting] = rules.aFailFallbackLevel;
      state.aAttempts[attempting] = 0;
    }
  }

  const result: HandResult = {
    finished, unfinished, winningTeam, losingTeam, upgrade,
    levelBefore, levelAfter, matchWon, heldAtA, aAttemptFailed, droppedToLevel,
  };
  state.lastResult = result;
  state.playingTeam = winningTeam;
  state.current = null;
  state.passes = 0;

  const events: GameEvent[] = [{ type: "handEnded", result }];
  if (matchWon) {
    state.phase = "matchOver";
    state.winner = winningTeam;
    events.push({ type: "matchEnded", winner: winningTeam });
  } else {
    state.phase = "handOver";
  }
  return events;
}

// --- views and automation ----------------------------------------------------

/** Everything one seat is allowed to know. This is what the AI reasons over. */
export interface SeatView {
  readonly seat: Seat;
  readonly rules: Ruleset;
  readonly level: Rank;
  readonly hand: readonly Card[];
  readonly current: { seat: Seat; combo: Combo } | null;
  readonly counts: readonly number[];
  readonly out: readonly boolean[];
  readonly finished: readonly Seat[];
  readonly discard: readonly Card[];
  readonly log: readonly PublicAction[];
  /** Actions in the currently live trick only. */
  readonly trick: readonly PublicAction[];
  readonly trickNumber: number;
  readonly tributeLog: readonly {
    kind: "paid" | "returned";
    from: Seat;
    to: Seat;
    card: Card | null;
  }[];
  readonly tribute: { readonly resisted: boolean; readonly payers: readonly Seat[] } | null;
  readonly turn: Seat;
  readonly phase: Phase;
}

export function viewFor(state: MatchState, seat: Seat): SeatView {
  return {
    seat,
    rules: state.rules,
    level: state.level,
    hand: [...(state.hands[seat] as Card[])],
    current: state.current === null ? null : { ...state.current },
    counts: state.hands.map((h) => h.length),
    out: [...state.out],
    finished: [...state.finished],
    discard: [...state.discard],
    log: state.log.map((entry) => ({
      ...entry,
      combo: entry.combo === null ? null : { ...entry.combo, cards: [...entry.combo.cards] },
    })),
    trick: state.log
      .filter((entry) => entry.trick === state.trickNumber)
      .map((entry) => ({
        ...entry,
        combo: entry.combo === null ? null : { ...entry.combo, cards: [...entry.combo.cards] },
      })),
    trickNumber: state.trickNumber,
    tributeLog: state.tributeLog.map((entry) => ({
      ...entry,
      card: entry.kind === "returned" && seat !== entry.from && seat !== entry.to ? null : entry.card,
    })),
    tribute: state.tribute === null
      ? null
      : { resisted: state.tribute.resisted, payers: [...state.tribute.payers] },
    turn: state.turn,
    phase: state.phase,
  };
}

/**
 * Rebuild a legal simulation state from one seat's public view and a sampled
 * assignment of the four live hands. Hidden match-scoring fields are neutral:
 * search stops at the end of this hand and reads the real hand result.
 */
export function hypotheticalState(view: SeatView, hands: readonly (readonly Card[])[]): MatchState {
  const copiedHands = SEATS.map((seat) => [...(hands[seat] ?? [])]);
  copiedHands[view.seat] = [...view.hand];
  const lastPlay = view.trick.findLastIndex((entry) => entry.kind === "play");
  const trailingPasses = view.trick.slice(lastPlay + 1).filter((entry) => entry.kind === "pass").length;
  return {
    seed: "hypothetical",
    rules: view.rules,
    phase: view.phase,
    handNumber: 1,
    levels: [view.level, view.level],
    aAttempts: [0, 0],
    playingTeam: teamOf(view.seat),
    level: view.level,
    hands: copiedHands,
    discard: [...view.discard],
    log: view.log.map((entry) => ({
      ...entry,
      combo: entry.combo === null ? null : { ...entry.combo, cards: [...entry.combo.cards] },
    })),
    trickNumber: view.trickNumber,
    tributeLog: view.tributeLog.map((entry) => ({ ...entry })),
    turn: view.turn,
    current: view.current === null
      ? null
      : { seat: view.current.seat, combo: { ...view.current.combo, cards: [...view.current.combo.cards] } },
    passes: trailingPasses,
    finished: [...view.finished],
    out: [...view.out],
    tribute: view.tribute === null
      ? null
      : { resisted: view.tribute.resisted, payers: [...view.tribute.payers], paid: [], owed: [], leader: view.turn },
    lastResult: null,
    winner: null,
  };
}

/** Legal plays for the seat on turn. */
export function playsFor(state: MatchState, seat: Seat): Combo[] {
  return legalPlays(
    state.hands[seat] as Card[],
    state.current === null ? null : state.current.combo,
    state.level,
    state.rules,
  );
}

/**
 * What to do for a seat that ran out of time: pass when that is allowed, and
 * otherwise shed the cheapest legal play so the hand keeps moving.
 */
export function autoAction(state: MatchState, seat: Seat): Action {
  if (state.phase === "returnTribute") {
    const hand = state.hands[seat] as Card[];
    const allowed = hand.filter((c) => rankOf(c) <= state.rules.maxReturnTributeRank);
    const pool = allowed.length > 0 ? allowed : hand;
    let lowest = pool[0] as Card;
    for (const card of pool) {
      if (singleOrder(rankOf(card), state.level) < singleOrder(rankOf(lowest), state.level)) lowest = card;
    }
    return { type: "returnTribute", seat, card: lowest };
  }
  if (state.current !== null) return { type: "pass", seat };
  const plays = playsFor(state, seat);
  let cheapest: Combo | null = null;
  for (const play of plays) {
    if (play.power > 0) continue;
    if (cheapest === null || play.cards.length < cheapest.cards.length ||
        (play.cards.length === cheapest.cards.length && play.rank < cheapest.rank)) {
      cheapest = play;
    }
  }
  const chosen = cheapest ?? plays[0];
  if (chosen === undefined) return { type: "pass", seat };
  return { type: "play", seat, cards: [...chosen.cards] };
}

export { beats, ComboType, type Combo };
